import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { request } from "../../utils/request";
import { auth } from "../../utils/auth";
import { formatCurrency, getModalidadAusencia } from "../../utils/helpers";
import dayjs from "dayjs";

const useData = () => {
    const [ausencias, setAusencias] = useState([]);

    const location = useLocation();

    const getAusencias = useCallback(() => {
		const searchString = location.search === "" ? "?paginate=100000" : location.search + "&paginate=1000000";

		request(`ausencias${searchString}`).then((res) => {
			setAusencias(res.data);
		});
	}, [location]);

    useEffect(() => {
        getAusencias();
    }, [getAusencias]);

    useEffect(() => {
		if (ausencias.length === 0) return;

		const timeout = setTimeout(() => {
			window.print();
		}, 2000);

		return () => {
			clearTimeout(timeout);
		};
	}, [ausencias]);

    return useMemo(
		() => ({
			ausencias,
		}),
		[ausencias]
	);
};

const renderValues = (value, ausencia, pendiente) => {
	if (pendiente) return ausencia.estado === 'Pendiente' ? value : '-';

	return ausencia.estado !== 'Pendiente' ? value : '-';
};

const tiempoTrabajando = (ingreso) => {
	const start = dayjs(ingreso);
	const end = dayjs();

	const years = end.diff(start, "year");
	const months = end.diff(start.add(years, "year"), "month");

	return `${years} años y ${months} meses`;
};

const calcularMonto = (empleado, cantidad_dia) => {
	const sueldoDia = parseFloat(empleado.sueldoFijo) / 23.83;

	return formatCurrency(sueldoDia * cantidad_dia);
};

const VacacionesPrint = () => {
	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);
    const { ausencias } = useData();
	const [showPendienteCols, setShowPendienteCols] = useState(true);
	const [showVacacionesCols, setShowVacacionesCols] = useState(true);

	useEffect(() => {
			if (!ausencias?.length) {
				setShowPendienteCols(true);
				setShowVacacionesCols(true);
				return;
			}

			const ausenciaTodasPendientes = ausencias?.every(ausencia => ausencia.estado === 'Pendiente') ?? false;
			const ausenciaAlgunaPendientes = ausencias?.some(ausencia => ausencia.estado === 'Pendiente') ?? false;
			
			setShowPendienteCols(ausenciaAlgunaPendientes);
			setShowVacacionesCols(!ausenciaTodasPendientes);
	}, [ausencias]);

    const user = auth.getUser();
	const sucursal = user.sucursales.filter(
		(suc) => suc.id === user.sucursalActual
	)[0];

    return (
		<>
			<div className="row">
				<h2 className="col-md-12 text-center m-b-5">
					Reporte Vacaciones
				</h2>
				<div className="col-md-12 text-center">
					{sucursal.nombre} - {sucursal.direccion} •{" "}
					{queryParams.get("desde")
						? `${dayjs(queryParams.get("desde")).format(
								"DD/MM/YYYY"
						  )} - `
						: ""}{" "}
					{dayjs(queryParams.get("hasta")).format("DD/MM/YYYY")}
				</div>
			</div>
			<div className="row mt-5 ml-2">
				<table className="table m-b-15">
					<thead>
						<tr>
							<th>Empleado</th>
							{showPendienteCols && <th>Fecha de Ingreso</th>}
							{showPendienteCols && <th>Tiempo Trabajando</th>}
							{showPendienteCols && <th>Dias Disponibles</th>}
							{showPendienteCols && <th>Monto a Pagar</th>}

							{showVacacionesCols && <th>Modalidad</th>}
							{showVacacionesCols && <th>Desde</th>}
							{showVacacionesCols && <th>Hasta</th>}
							{showVacacionesCols && <th>Días</th>}
							{showVacacionesCols && <th>Sábado</th>}
							{showVacacionesCols && <th>Pagar</th>}
							{showVacacionesCols && <th>Descripción</th>}
							{showVacacionesCols && <th>Desc.</th>}
							<th>Estado</th>
						</tr>
					</thead>

					<tbody>
						{ausencias.map((ausencia) => {
							return (
								<tr key={ausencia.id}>
									<td>
										{ausencia.empleado?.nombreCompleto}
										{ausencia.estado !== "Pendiente" && <>
											<br/>
											<small>{tiempoTrabajando(ausencia.empleado?.fechaIngreso)}</small>
										</>}
									</td>

									{showPendienteCols && <td>{renderValues(dayjs(ausencia.empleado?.fechaIngreso).format("DD/MM/YYYY"), ausencia, true)}</td>}
									{showPendienteCols && <td>{renderValues(tiempoTrabajando(ausencia.empleado?.fechaIngreso), ausencia, true)}</td>}
									{showPendienteCols && <td>{renderValues(ausencia.restantes, ausencia, true)}</td>}
									{showPendienteCols && <td>{renderValues(calcularMonto(ausencia.empleado, ausencia.restantes), ausencia, true)}</td>}
									
									{showVacacionesCols && <td>{renderValues(getModalidadAusencia(ausencia.modalidad)?.label, ausencia, false)}</td>}
									{showVacacionesCols && <td>{renderValues(dayjs(ausencia.desde).format("DD/MM/YYYY"), ausencia, false)}</td>}
									{showVacacionesCols && <td>{renderValues(dayjs(ausencia.hasta).format("DD/MM/YYYY"), ausencia, false)}</td>}
									{showVacacionesCols && <td style={{width: '60px', padding: '5px'}}>{renderValues(<>
										<span title="Total de días">T-{ausencia.cantidad_dia}</span> <br/>
										<span title="Días pendientes">P-{ausencia.restantes?.toString()}</span></>, ausencia, false)}
									</td>}
									{showVacacionesCols && <td>{renderValues(ausencia.contar_sabado === 0 ? "No Contar" : (ausencia.contar_sabado === 1 ? "Contar 1 Día" : "Contar 1/2 Día"), ausencia, false)}</td>}
									{showVacacionesCols && <td>{renderValues(formatCurrency(ausencia.monto_pagar), ausencia, false)}</td>}
									{showVacacionesCols && <td style={{maxWidth: '256px', padding: '5px', overflowWrap: 'break-word'}}>{ausencia.descripcion}</td>}
									{showVacacionesCols && <td>{renderValues(ausencia.descontar_vacaciones ? "Si" : "No", ausencia, false)}</td>}
									<td style={{textTransform: 'capitalize'}}>{ausencia.estado}</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>

			<style type="text/css" media="print">
				@page {"{ size: landscape; }"}
			</style>
        </>
    );
};

export default VacacionesPrint;
